package com.milta.feature.route.data.repository

import com.milta.core.network.ApiException
import com.milta.feature.route.data.local.PendingDeliveryValidationLocalDataSource
import com.milta.feature.route.domain.entity.DeliveryOutcome
import com.milta.feature.route.domain.entity.DeliveryProof
import com.milta.feature.route.domain.entity.PendingDeliveryValidation
import com.milta.feature.route.domain.entity.PendingSyncState
import com.milta.feature.route.domain.repository.OfflineSyncRepository
import com.milta.feature.route.domain.repository.RouteRepository
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock

/**
 * MIL-43 — replays [PendingDeliveryValidation]s against the real [RouteRepository.validateDelivery]
 * (same endpoint as the online flow, MIL-37/41). Depends on the domain [RouteRepository] rather
 * than [com.milta.feature.route.data.remote.RouteApi] directly to reuse its existing DTO
 * mapping/wire-value conversion instead of duplicating it here.
 */
class OfflineSyncRepositoryImpl(
    private val routeRepository: RouteRepository,
    private val localDataSource: PendingDeliveryValidationLocalDataSource,
) : OfflineSyncRepository {

    override val pending: StateFlow<List<PendingDeliveryValidation>> = localDataSource.items

    override fun enqueue(
        routeId: String,
        deliveryId: String,
        recipientName: String,
        address: String,
        status: DeliveryOutcome,
        comment: String?,
        photoBytes: ByteArray?,
        photoFileName: String?,
        occurredAt: String,
        capturedAtLabel: String,
    ): PendingDeliveryValidation = localDataSource.add(
        routeId = routeId,
        deliveryId = deliveryId,
        recipientName = recipientName,
        address = address,
        status = status,
        comment = comment,
        photoBytes = photoBytes,
        photoFileName = photoFileName,
        occurredAt = occurredAt,
        capturedAtLabel = capturedAtLabel,
    )

    // Two cycles can start together (network back + mandatory update sending the queue first): run
    // one at a time, the second then only sees what the first left — never two POSTs for one item
    // (the second one would 409 and wrongly turn the item into a Conflict).
    private val syncMutex = Mutex()

    override suspend fun syncPending(): List<DeliveryProof> = syncMutex.withLock { syncCycle() }

    private suspend fun syncCycle(): List<DeliveryProof> {
        val synced = mutableListOf<DeliveryProof>()
        // Snapshot at cycle start — sequential replay, capture order (mockup screen 3, "1/2
        // livraisons envoyées"). Items enqueued *during* this cycle (a driver validating a new stop
        // while the sync is running) are picked up by the next cycle, not this one.
        val snapshot = localDataSource.items.value.filter { it.syncState != PendingSyncState.Conflict }
        for (item in snapshot) {
            localDataSource.updateSyncState(item.id, PendingSyncState.Syncing)
            // Retour 27/10 (point 3) — no file name = queued without photo (driver's own route).
            val bytes = item.photoFileName?.let { localDataSource.photoBytesFor(item.id) }
            if (item.photoFileName != null && bytes == null) {
                // Should never happen — bytes are added atomically with the entity in `enqueue`.
                // Drop defensively rather than retrying forever against a payload that no longer exists.
                localDataSource.remove(item.id)
                continue
            }
            runCatching {
                routeRepository.validateDelivery(
                    routeId = item.routeId,
                    deliveryId = item.deliveryId,
                    photoBytes = bytes,
                    photoFileName = item.photoFileName,
                    status = item.status,
                    comment = item.comment,
                    occurredAt = item.occurredAt,
                )
            }.fold(
                onSuccess = { proof ->
                    synced += proof
                    localDataSource.remove(item.id)
                },
                onFailure = { e ->
                    val apiException = e as? ApiException
                    val code = apiException?.message
                    // 409 = the delivery already has a final status server-side (e.g. synced from
                    // another device) — a real HTTP conflict, not a transient network/server error,
                    // so retrying it again next cycle would never succeed.
                    val state = if (apiException?.statusCode == 409) PendingSyncState.Conflict else PendingSyncState.Failed
                    localDataSource.updateSyncState(item.id, state, code)
                },
            )
        }
        return synced
    }

    override fun dismiss(id: String) = localDataSource.remove(id)
}
